import { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { hasAllRequiredPermissions, ROUTES } from '@/constants'
import { useI18n } from '@/i18n/useI18n'
import { useUserStore } from '@/store'

interface RequirePermissionsProps {
  requiredPermissions: readonly string[]
  children: ReactNode
}

export function RequirePermissions({
  requiredPermissions,
  children,
}: RequirePermissionsProps) {
  const { t } = useI18n()
  const permissions = useUserStore(
    (state) => state.currentUser?.permissions ?? [],
  )
  const location = useLocation()

  if (hasAllRequiredPermissions(permissions, requiredPermissions)) {
    return <>{children}</>
  }

  if (location.pathname === ROUTES.DASHBOARD) {
    return <Navigate to={ROUTES.POS.CHECKOUT} replace />
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          {t('auth.forbidden.title')}
        </h1>
        <p className="mt-1 text-muted-foreground">
          {t('auth.forbidden.description')}
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>{t('auth.forbidden.restricted')}</CardTitle>
          <CardDescription>
            {t('auth.forbidden.noPermission')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {t('auth.forbidden.currentPath')}{' '}
            <code className="text-xs">{location.pathname}</code>
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
